import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Edit, Trash2, Archive, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from '@/components/ui/toaster';
import { useAuth, useHasRole } from '@/features/auth/AuthProvider';
import type { Supplier } from '@/types/schemas';
import {
  archiveSupplier,
  createSupplier,
  deleteSupplier,
  fetchSuppliers,
  fetchSupplierPurchases,
  addSupplierPurchaseEntry,
  updateSupplier,
} from '../supplierQueries';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export function SuppliersTab() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canWrite = useHasRole('owner');
  const canDelete = useHasRole('owner');

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Supplier | null>(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [purchaseAmount, setPurchaseAmount] = useState(0);
  const [purchaseDesc, setPurchaseDesc] = useState('');
  const [purchaseType, setPurchaseType] = useState<'PURCHASE' | 'ADVANCE'>('PURCHASE');

  const { data: suppliers = [] } = useQuery({
    queryKey: ['suppliers'],
    queryFn: fetchSuppliers,
  });

  const { data: purchases = [] } = useQuery({
    queryKey: ['supplier-purchases', selectedId],
    queryFn: () => (selectedId ? fetchSupplierPurchases(selectedId) : Promise.resolve([])),
    enabled: !!selectedId,
  });

  const save = useMutation({
    mutationFn: async () => {
      if (!name.trim()) throw new Error('Name required');
      if (editing) {
        await updateSupplier(editing.id, { name: name.trim(), contactPhone: phone, notes });
      } else {
        await createSupplier({
          name: name.trim(),
          contactPhone: phone,
          notes,
          totalAmount: 0,
          advanceAmount: 0,
          isActive: true,
        });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['suppliers'] });
      toast({ title: 'Supplier saved', variant: 'success' });
      setOpen(false);
    },
    onError: (e) => toast({ title: 'Failed', description: String(e), variant: 'destructive' }),
  });

  const archive = useMutation({
    mutationFn: (id: string) => archiveSupplier(id, user?.uid ?? ''),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['suppliers'] }),
  });

  const remove = useMutation({
    mutationFn: deleteSupplier,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['suppliers'] }),
  });

  const addPurchase = useMutation({
    mutationFn: async () => {
      if (!selectedId) throw new Error('Select supplier');
      await addSupplierPurchaseEntry(
        {
          supplierId: selectedId,
          type: purchaseType,
          amount: purchaseAmount,
          description: purchaseDesc,
        },
        user?.uid ?? '',
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['supplier-purchases'] });
      qc.invalidateQueries({ queryKey: ['suppliers'] });
      toast({ title: 'Entry recorded', variant: 'success' });
      setPurchaseDesc('');
      setPurchaseAmount(0);
    },
    onError: (e) => toast({ title: 'Failed', description: String(e), variant: 'destructive' }),
  });

  function openEdit(s: Supplier | null) {
    setEditing(s);
    setName(s?.name ?? '');
    setPhone(s?.contactPhone ?? '');
    setNotes(s?.notes ?? '');
    setOpen(true);
  }

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div>
        <div className="mb-3 flex justify-between items-center">
          <h3 className="font-medium">Suppliers</h3>
          {canWrite && (
            <Button size="sm" variant="gold" onClick={() => openEdit(null)}>
              <Plus className="h-4 w-4" /> Add
            </Button>
          )}
        </div>
        <div className="space-y-2">
          {suppliers.map((s) => (
            <Card
              key={s.id}
              className={`p-3 cursor-pointer ${selectedId === s.id ? 'ring-2 ring-gold-400' : ''}`}
              onClick={() => setSelectedId(s.id)}
            >
              <div className="flex justify-between gap-2">
                <div>
                  <p className="font-medium">{s.name}</p>
                  <p className="text-xs text-muted-foreground">
                    Purchases: ₹{s.totalAmount} · Advance: ₹{s.advanceAmount}
                  </p>
                </div>
                <div className="flex gap-0.5">
                  {canWrite && (
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); openEdit(s); }}>
                      <Edit className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {canWrite && (
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); archive.mutate(s.id); }}>
                      <Archive className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {canDelete && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (confirm('Delete supplier?')) remove.mutate(s.id);
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      <Card className="p-4">
        <h3 className="font-medium mb-3">Purchase ledger</h3>
        {!selectedId && <p className="text-sm text-muted-foreground">Select a supplier.</p>}
        {selectedId && canWrite && (
          <div className="mb-4 grid gap-2 sm:grid-cols-2">
            <div>
              <Label>Type</Label>
              <select className="h-9 w-full rounded-md border px-2 text-sm" value={purchaseType} onChange={(e) => setPurchaseType(e.target.value as 'PURCHASE' | 'ADVANCE')}>
                <option value="PURCHASE">Purchase</option>
                <option value="ADVANCE">Advance</option>
              </select>
            </div>
            <div>
              <Label>Amount</Label>
              <Input type="number" min={0} value={purchaseAmount} onChange={(e) => setPurchaseAmount(Number(e.target.value))} />
            </div>
            <div className="sm:col-span-2">
              <Label>Description</Label>
              <Input value={purchaseDesc} onChange={(e) => setPurchaseDesc(e.target.value)} />
            </div>
            <Button variant="gold" size="sm" disabled={addPurchase.isPending} onClick={() => addPurchase.mutate()}>
              Add entry
            </Button>
          </div>
        )}
        <ul className="space-y-2 max-h-[40vh] overflow-y-auto text-sm">
          {purchases.map((p) => (
            <li key={p.id} className="rounded border px-3 py-2">
              <div className="flex justify-between">
                <span>{p.type}</span>
                <span>₹{p.amount}</span>
              </div>
              <p className="text-xs text-muted-foreground">{p.description}</p>
            </li>
          ))}
        </ul>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit supplier' : 'New supplier'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            <div>
              <Label>Notes</Label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="gold" disabled={save.isPending} onClick={() => save.mutate()}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
